import { CreatedByType } from './Common';
import { RichContentData } from './richContent';

export enum QuestionTypeEnum {
  RADIOBUTTON = 'RADIOBUTTON',
  CHECKBOX = 'CHECKBOX',
  DROPDOWN = 'DROPDOWN',
  SLIDER = 'SLIDER',
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  HOUR = 'HOUR',
  DATE = 'DATE',
  // Answerless full-page rich content: rendered via ContentRenderer, no answer input,
  // always valid, never POSTed, no export column.
  INFORMATION = 'INFORMATION',
}

export enum QuestionTextTypeEnum {
  EMAIL = 'EMAIL',
  PARAGRAPH = 'PARAGRAPH',
  LINE = 'LINE'
}

export type DetailInterfaceByQuestionTypes = {
  CHECKBOX: CheckboxQuestionDetailsType,
  RADIOBUTTON: CommonQuestionDetailsType,
  DATE: DateQuestionDetailsType,
  DROPDOWN: CommonQuestionDetailsType,
  HOUR: HourQuestionDetailsType,
  NUMBER: NumberQuestionDetailsType,
  SLIDER: SliderQuestionDetailsType,
  TEXT: TextQuestionDetailsType,
  INFORMATION: InformationQuestionDetailsType,
};

// Resolved content object served with an INFORMATION question (same wire shape as a
// section intro: the rich blocks live under .content).
export interface InformationQuestionContentType {
  id: number;
  label: string;
  content: RichContentData;
  page_style?: string | null;
}

export interface InformationQuestionDetailsType extends CoreQuestionDetailsType {
  content_id?: number;
  content?: InformationQuestionContentType | null;
}

// TODO: define integral_errorcode
interface IntegralType {
  is_integral: boolean,
  integral_errorcode: null
}

// Runtime-only context attached to a question when the set engine clones it
// into a concrete instance (a checkbox option set, or one number repetition).
export interface RuntimeInstance {
  /** Stable identity for this instance within a run (used to move through the set). */
  key: string;
  /**
   * The session key its answer is stored under: the instance key when it gets its own
   * answer (number repetition, choice of a "repeat for each choice" group), null when it
   * has a single plain answer ("ask once" group member). Absent = same as `key`.
   */
  answerKey?: string | null;
  /** The set driver question this instance belongs to. */
  driverId: number;
  /**
   * Sent as the answer's instance_index: 1..N for number repeats, the choice id for a
   * "repeat for each choice" group, 0 (a plain answer) for an "ask once" group.
   */
  instanceIndex: number;
  /** The checkbox option that produced this instance (null for number repeats and "ask once" groups). */
  triggerAnswerId: number | null;
  /** Heading shown above the first question of the instance ("First MRI", option label…). */
  heading: string | null;
}

export interface CoreQuestionType {
  created_by?: CreatedByType;
  last_update_by?: CreatedByType;
  id: number;
  description: string;
  export_code: string;
  integral: IntegralType;
  is_mandatory_question: boolean;
  is_active: boolean;
  score_coefficient: number;
  order: number;
  // null / undefined = section-less (normal flow)
  section_id?: number | null;
  // Follow-up set membership (configured in Diapason); null on top-level questions.
  set_driver_question_id?: number | null;
  set_trigger_answer_id?: number | null;
  set_order?: number | null;
  // Multi-choice (checkbox) follow-up group of a member: its group number (the order groups
  // are asked in), the group's choices, and whether it is asked once or for each ticked choice.
  set_group?: number | null;
  set_trigger_answer_ids?: number[] | null;
  set_ask_once?: boolean;
  // Identity question flag (API-side divert: the answer is stored encrypted in the subject's
  // profile, not in the answers table). Resonance renders/submits normally; prefill on resume
  // arrives as a synthesized answer from the API.
  identity_target?: string | null;
  // Runtime-only: present on instance clones produced by the set engine.
  runtime?: RuntimeInstance;
  title: string;
  version: string;
  type: string;
}

// TODO: define rules object
interface CoreQuestionDetailsType {
  created_by?: CreatedByType;
  last_update_by?: CreatedByType;
  id?: number;
  version?: number;
}

interface CoreAnswerType {
  created_by?: CreatedByType;
  last_update_by?: CreatedByType;
  id: number;
  order?: number;
}

export interface CommonQuestionDetailsType extends CoreQuestionDetailsType {
  answers: CommonAnswersType[];
}

export interface CommonAnswersType extends CoreAnswerType {
  label: string;
}

export interface CheckboxQuestionDetailsType extends CoreQuestionDetailsType {
  answers: CheckboxAnswerType[];
  rules: CheckboxRulesType;
}

export interface CheckboxAnswerType extends CoreAnswerType {
  label: string;
  variable_name: string;
}

export interface RadioQuestionType extends CoreQuestionType {
  type: QuestionTypeEnum.RADIOBUTTON;
  detail: CommonQuestionDetailsType;
}

export interface CheckboxQuestionType extends CoreQuestionType {
  type: QuestionTypeEnum.CHECKBOX;
  detail: CheckboxQuestionDetailsType;
}

export interface DropdownQuestionType extends CoreQuestionType {
  type: QuestionTypeEnum.DROPDOWN;
  detail: CommonQuestionDetailsType;
}

export interface SliderQuestionType extends CoreQuestionType {
  type: QuestionTypeEnum.SLIDER;
  detail: SliderQuestionDetailsType;
}

export interface SliderQuestionDetailsType extends CoreQuestionDetailsType {
  minor_tick_interval: number;
  prefix: string;
  step: number;
  suffix: string;
  tick_interval: number;
  answers: SliderAnswerType[];
}

export interface SliderAnswerType extends CoreAnswerType {
  asset: string;
  /** Optional, like the asset: a point may carry a label, an image, both or neither. */
  label?: string | null;
  value: number;
}

export interface TextQuestionType extends CoreQuestionType {
  type: QuestionTypeEnum.TEXT;
  detail: TextQuestionDetailsType;
}

export interface TextQuestionDetailsType extends CoreQuestionDetailsType {
  type: QuestionTextTypeEnum,
  // Optional per-question placeholder (LINE inputs). Absent ⇒ the generic
  // "answer_text_placeholder" copy — real questionnaire questions never set
  // it; synthetic ones (e.g. the NIR field) use it to show the expected format.
  placeholder?: string,
}

export interface NumberQuestionDetailsType extends CoreQuestionDetailsType {
  min_value: number;
  max_value: number;
  prefix: string;
  suffix: string;
  number_decimal: number;
  separator: NumberSeparatorEnum;
  // Heading template for the repeating set, e.g. "{ordinal} MRI".
  repetition_label?: string | null;
}

export interface HourQuestionDetailsType extends CoreQuestionDetailsType {
  format: HourFormatEnum,
  min_hour: string,
  max_hour: string
}

export interface DateQuestionDetailsType extends CoreQuestionDetailsType {
  format: DateFormatEnum;
  min_date: DateConstraintType;
  max_date: DateConstraintType;
}

export type DateConstraintType = (FixedDateType | RelativeDateType | DisableDateType) & { isMinDate?: boolean };

export interface FixedDateType {
  date_type: DateConstraintEnum.FIXED;
  todays_date_sens: null;
  todays_date_quantity: null;
  todays_date_unit: null;
  fixed_date: string;
}

export interface RelativeDateType {
  date_type: DateConstraintEnum.RELATIVE;
  todays_date_sens: DateSensEnum;
  todays_date_quantity: number;
  todays_date_unit: DateUnitEnum;
  fixed_date: null;
}

export interface DisableDateType {
  date_type: DateConstraintEnum.DISABLED;
  todays_date_sens: null;
  todays_date_quantity: null;
  todays_date_unit: null;
  fixed_date: null;
}

export type QuestionType =
  RadioQuestionType |
  CheckboxQuestionType |
  DropdownQuestionType |
  SliderQuestionType |
  TextQuestionType |
  NumberQuestionType |
  HourQuestionType |
  DateQuestionType |
  InformationQuestionType;

export type QuestionDetailType =
  CommonQuestionDetailsType |
  NumberQuestionDetailsType |
  TextQuestionDetailsType |
  DateQuestionDetailsType |
  SliderQuestionDetailsType |
  HourQuestionDetailsType |
  DateQuestionDetailsType |
  InformationQuestionDetailsType;

export interface InformationQuestionType extends CoreQuestionType {
  type: QuestionTypeEnum.INFORMATION;
  detail: InformationQuestionDetailsType;
}

export interface NumberQuestionType extends CoreQuestionType {
  type: QuestionTypeEnum.NUMBER;
  detail: NumberQuestionDetailsType;
}

export interface HourQuestionType extends CoreQuestionType {
  type: QuestionTypeEnum.HOUR;
  detail: HourQuestionDetailsType;
}

export interface DateQuestionType extends CoreQuestionType {
  type: QuestionTypeEnum.DATE;
  detail: DateQuestionDetailsType;
}

export enum NumberSeparatorEnum {
  DOT = 'dot',
  COMMA = 'comma'
}

export enum HourFormatEnum {
  '12HOURS' = '12HOURS',
  '24HOURS' = '24HOURS',
  '24HOURS-AM/PM' = '24HOURS-AM/PM'
}

export enum DateFormatEnum {
  DAY = 'DAY',
  MONTH = 'MONTH',
  YEAR = 'YEAR'
}

export enum DateConstraintEnum {
  RELATIVE = 'RELATIVE_DATE',
  FIXED = 'FIXED_DATE',
  DISABLED = 'DISABLED',
  NO_LIMIT = 'NO_LIMIT'
}

export enum DateSensEnum {
  MINUS = 'MINUS',
  PLUS = 'PLUS'
}

export enum DateUnitEnum {
  DAY = 'DAY',
  MONTH = 'MONTH',
  YEAR = 'YEAR'
}

export interface CheckboxRulesType {
  constraint: CheckboxRulesEnum;
  quantity: number;
}

export enum CheckboxRulesEnum {
  NONE = 'NONE',
  AT_LEAST = 'AT_LEAST',
  EXACTLY = 'EXACTLY',
  AT_MOST = 'AT_MOST'
}
